import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import apiService from 'services/api';
import courseService from 'services/course';
import useBeforeUnload from 'services/hooks/useBeforeUnload';
import {
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { compareCanvasRubrics } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const { auth } = useAuth();
    const {
        course,
        setAssignments,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
    } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const location = useLocation();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState({
        title: '',
        description: '',
        rubric: null,
        baseline: null,
        citationFormat: CITATION_SELECT_OPTIONS[0].key,
        contentBankResources: [],
        aiFeedbackEnabled: true,
        feedbackSettings: {
            structure: DEFAULT_FEEDBACK_STRUCTURE,
            // used when structure is rubric or system
            numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
            suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
            languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
            tone: DEFAULT_FEEDBACK_TONE,
            // used when structure is templated
            templated: {},
            // used when structure is custom
            customInstructions: '',
        },
        feedbackCustomContext: '',
        aiGradingEnabled: true,
        gradingSettings: null,
        gradingCustomContext: '',
        aiStudentFeedbackEnabled: true,
        aiInlineCommentsEnabled: true,
        inlineCommentsSettings: {
            maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
            commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
            languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
            tone: DEFAULT_INLINE_COMMENTS_TONE,
        },
        inlineCommentsCustomContext: '',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectRubricDialog, setSelectRubricDialog] = useState({ open: false });
    const [createRubricDialog, setCreateRubricDialog] = useState({ open: false });
    const [createRubricAIDialog, setCreateRubricAIDialog] = useState({ open: false });
    const [editRubricDialog, setEditRubricDialog] = useState({ open: false, customDefaultForm: null });
    const [editBaselineDialog, setEditBaselineDialog] = useState({ open: false });
    const [uploadContentBankResourcesDialog, setUploadContentBankResourcesDialog] = useState({ open: false });
    const [editContentBankResourcesDialog, setEditContentBankResourcesDialog] = useState({ open: false });
    const [feedbackSettingsDialog, setFeedbackSettingsDialog] = useState({ open: false });
    const [inlineCommentsSettingsDialog, setInlineCommentsSettingsDialog] = useState({ open: false });
    const [gradingSettingsDialog, setGradingSettingsDialog] = useState({ open: false });
    const [previewGradingDialog, setPreviewGradingDialog] = useState({ open: false });
    const [pushRubricDialog, setPushRubricDialog] = useState({ open: false });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const isFeedbackSettingsDefault = (form) => {
        if (form.structure === 'rubric') {
            return (
                form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
                form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
                form.tone === DEFAULT_FEEDBACK_TONE
            );
        }
        if (form.structure === 'system') {
            return (
                form.numSuggestions === DEFAULT_FEEDBACK_NUM_SUGGESTIONS &&
                form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
                form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
                form.tone === DEFAULT_FEEDBACK_TONE
            );
        }
        return false;
    };

    const isInlineCommentsSettingsDefault = (form) => {
        return (
            form?.maxCommentsPerPage === DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE &&
            form?.commentLength === DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH &&
            form?.languageLevel === DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL &&
            form?.tone === DEFAULT_INLINE_COMMENTS_TONE
        );
    };

    const validateAll = useCallback(() => {
        let isValid = true;
        let newErrors = {};

        if (!form.title.trim()) {
            isValid = false;
            newErrors = { ...newErrors, title: true };
        }

        if (!form.description.trim()) {
            isValid = false;
            newErrors = { ...newErrors, description: true };
        }

        if (!form.rubric) {
            isValid = false;
            newErrors = { ...newErrors, rubric: true };
        }

        if (errors.feedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, feedbackSettings: errors.feedbackSettings };
        }

        if (errors.gradingSettings) {
            isValid = false;
            newErrors = { ...newErrors, gradingSettings: errors.gradingSettings };
        }

        setErrors(newErrors);

        return {
            isValid,
            errors: newErrors,
        };
    }, [errors.feedbackSettings, errors.gradingSettings, form.description, form.rubric, form.title]);

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                history.push('/assignments');
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const publishAssignment = useCallback(
        async (api_data, rubric) => {
            setHasClickedSubmit(true);
            const { isValid, errors } = validateAll();

            if (!isValid) {
                return setDialog({
                    open: true,
                    message: t(
                        'dialog.incomplete-assignment-warning.title',
                        'Please complete all missing required fields before publishing.'
                    ),
                    description: (
                        <ul>
                            {errors.title && (
                                <li style={{ textAlign: 'left' }}>{t('assignment-title-label', 'Assignment title')}</li>
                            )}
                            {errors.description && (
                                <li style={{ textAlign: 'left' }}>
                                    {t(
                                        'dialog.incomplete-assignment-warning.assignment-description',
                                        'Assignment description'
                                    )}
                                </li>
                            )}
                            {errors.rubric && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.assignment-rubric', 'Assignment rubric')}
                                </li>
                            )}
                            {errors.feedbackSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.feedback-settings', 'Feedback settings')}
                                </li>
                            )}
                            {errors.gradingSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                </li>
                            )}
                        </ul>
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.ok', 'Ok'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }

            setIsSaving(true);
            const timestamp = new Date().getTime();
            const assignment = {
                id: uuid(),
                active: true,
                owner_account_id: auth.id,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: rubric || form.rubric,
                baseline: form.baseline,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_feedback_enabled: form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_grading_enabled: form.aiGradingEnabled && !isGradingFeatureDisabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: false,
                has_seen_preview: false,
                has_finished_preview: false,
                preview_files: [],
            };
            if (form?.api_assignment_id) {
                assignment.api_assignment_id = form.api_assignment_id;
                assignment.api_data = api_data || form.api_data;
            }
            await courseService.createCourseAssignment(course.id, assignment);
            setAssignments((current) => [...current, assignment]);
            message.success(t('message.create-success', 'Your assignment was successfully created.'));
            history.push('/assignments');
            setLoading(false);
        },
        [
            auth?.id,
            closeDialog,
            course?.id,
            form.aiFeedbackEnabled,
            form.aiGradingEnabled,
            form.aiInlineCommentsEnabled,
            form.aiStudentFeedbackEnabled,
            form.api_assignment_id,
            form.api_data,
            form.baseline,
            form.citationFormat,
            form.contentBankResources,
            form.description,
            form.feedbackCustomContext,
            form.feedbackSettings,
            form.gradingCustomContext,
            form.gradingSettings,
            form.inlineCommentsCustomContext,
            form.inlineCommentsSettings,
            form.rubric,
            form.title,
            history,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            message,
            setAssignments,
            setDialog,
            validateAll,
            t,
        ]
    );

    const saveAssignment = useCallback(
        async (api_data, rubric) => {
            setIsSaving(true);
            const timestamp = new Date().getTime();
            const assignment = {
                id: uuid(),
                active: true,
                owner_account_id: auth.id,
                created_on: timestamp,
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: rubric || form.rubric,
                baseline: form.baseline,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_feedback_enabled: form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_grading_enabled: form.aiGradingEnabled && !isGradingFeatureDisabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: true,
                has_seen_preview: false,
                has_finished_preview: form.api_assignment_id ? true : false,
                preview_files: [],
            };
            if (form?.api_assignment_id) {
                assignment.api_assignment_id = form.api_assignment_id;
                assignment.api_data = api_data || form.api_data;
            }
            await courseService.createCourseAssignment(course.id, assignment);
            setAssignments((current) => [...current, assignment]);
            message.success(t('message.save-success', 'Your assignment was successfully saved.'));
            history.push('/assignments');
        },
        [
            t,
            auth?.id,
            course?.id,
            form.aiFeedbackEnabled,
            form.aiGradingEnabled,
            form.aiInlineCommentsEnabled,
            form.aiStudentFeedbackEnabled,
            form.baseline,
            form.citationFormat,
            form.contentBankResources,
            form.description,
            form.feedbackSettings,
            form.feedbackCustomContext,
            form.gradingCustomContext,
            form.gradingSettings,
            form.inlineCommentsCustomContext,
            form.inlineCommentsSettings,
            form.rubric,
            form.title,
            form.api_assignment_id,
            form.api_data,
            history,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            message,
            setAssignments,
        ]
    );

    const onClickSave = useCallback(
        async ({ key }) => {
            if (key === 'publish') {
                if (form.api_assignment_id && process.env.REACT_APP_KIND === 'CANVAS') {
                    const { isValid, errors } = validateAll();

                    if (!isValid) {
                        return setDialog({
                            open: true,
                            message: 'Please complete all missing required fields before publishing.',
                            description: (
                                <ul>
                                    {errors.title && <li style={{ textAlign: 'left' }}>Assignment title</li>}
                                    {errors.description && (
                                        <li style={{ textAlign: 'left' }}>Assignment description</li>
                                    )}
                                    {errors.rubric && <li style={{ textAlign: 'left' }}>Assignment rubric</li>}
                                    {errors.feedbackSettings && (
                                        <li style={{ textAlign: 'left' }}>Feedback settings</li>
                                    )}
                                    {errors.gradingSettings && <li style={{ textAlign: 'left' }}>Grading settings</li>}
                                </ul>
                            ),
                            note: '',
                            variant: 'destructive',
                            actionButtonText: 'Ok',
                            cancelButtonText: 'Cancel',
                            onAction: () => closeDialog(),
                            onCancel: () => closeDialog(),
                            onClose: () => closeDialog(),
                        });
                    }
                    setLoading(true);
                    setIsSaving(true);
                    const course_id = auth.course_id;
                    const accessToken = localStorage.getItem('access_token');
                    const APIAssignment = await apiService.getAssignment({
                        course_id,
                        accessToken,
                        assignment_id: form.api_assignment_id,
                    });
                    const is_matching = compareCanvasRubrics(form.rubric, APIAssignment.rubric);
                    if (!is_matching) {
                        return setPushRubricDialog({
                            open: true,
                            onSave: ({ api_data, rubric }) => {
                                if (rubric) {
                                    publishAssignment(api_data, rubric);
                                }
                            },
                            form,
                        });
                    } else {
                        publishAssignment();
                    }
                } else {
                    publishAssignment();
                }
            }
            if (key === 'save') {
                if (form.api_assignment_id && process.env.REACT_APP_KIND === 'CANVAS') {
                    setLoading(true);
                    setIsSaving(true);
                    const course_id = auth.course_id;
                    const accessToken = localStorage.getItem('access_token');
                    const APIAssignment = await apiService.getAssignment({
                        course_id,
                        accessToken,
                        assignment_id: form.api_assignment_id,
                    });
                    const is_matching = compareCanvasRubrics(form.rubric, APIAssignment.rubric);
                    if (!is_matching && form.rubric) {
                        return setPushRubricDialog({
                            open: true,
                            onSave: ({ api_data, rubric }) => {
                                if (rubric) {
                                    saveAssignment(api_data, rubric);
                                }
                            },
                            form,
                        });
                    } else {
                        saveAssignment();
                    }
                } else {
                    saveAssignment();
                }
            }
        },
        [auth.course_id, closeDialog, form, publishAssignment, saveAssignment, setDialog, validateAll]
    );

    useEffect(() => {
        if (hasClickedSubmit) {
            validateAll();
        }
    }, [hasClickedSubmit, validateAll]);

    useEffect(() => {
        if (location?.state?.assignment) {
            const { api_assignment_id, api_data, title, description, contentBankResources, rubric, citationFormat } =
                location.state?.assignment;
            setForm({
                title: title,
                description: description,
                rubric: rubric,
                baseline: null,
                aiFeedbackEnabled: true,
                feedbackSettings: {
                    structure: DEFAULT_FEEDBACK_STRUCTURE,
                    numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                    suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                    languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                    tone: DEFAULT_FEEDBACK_TONE,
                    customInstructions: '',
                },
                citationFormat,
                contentBankResources,
                feedbackCustomContext: '',
                aiGradingEnabled: true,
                gradingCustomContext: '',
                aiInlineCommentsEnabled: true,
                inlineCommentsSettings: {
                    maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
                    commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
                    languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
                    tone: DEFAULT_INLINE_COMMENTS_TONE,
                },
                inlineCommentsCustomContext: '',
                api_assignment_id: api_assignment_id,
                api_data: api_data,
            });
        }
    }, [location?.state]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        });

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, onBeforeNavigate, setDialog, t]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                loading,
                setLoading,
                form,
                setForm,
                errors,
                setErrors,
                selectRubricDialog,
                setSelectRubricDialog,
                createRubricDialog,
                setCreateRubricDialog,
                createRubricAIDialog,
                setCreateRubricAIDialog,
                editRubricDialog,
                setEditRubricDialog,
                editBaselineDialog,
                setEditBaselineDialog,
                uploadContentBankResourcesDialog,
                setUploadContentBankResourcesDialog,
                editContentBankResourcesDialog,
                setEditContentBankResourcesDialog,
                feedbackSettingsDialog,
                setFeedbackSettingsDialog,
                inlineCommentsSettingsDialog,
                setInlineCommentsSettingsDialog,
                gradingSettingsDialog,
                setGradingSettingsDialog,
                previewGradingDialog,
                setPreviewGradingDialog,
                pushRubricDialog,
                setPushRubricDialog,
                isSaving,
                setIsSaving,
                isFeedbackSettingsDefault,
                isInlineCommentsSettingsDefault,
                onClickBack,
                onClickSave,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
